import { IS_PROD } from '@diamond/shared/environments';
import { DetailProduct, OrderTemplateItemProduct } from '@diamond/shared/types';
import { getAnalytics, logEvent } from 'firebase/analytics';

import { BannerRefererParams, getBannerReferer } from './banner-referer';
import { getBuyerAnalyticsContext } from './buyer-analytics-context';
import { getFirebaseApp } from './firebase/firebase-config';

const getBannerRefererSafe = (): BannerRefererParams | null => {
  try {
    return getBannerReferer();
  } catch (error) {
    return null;
  }
};

/**
 * Sanitizes a string by replacing non-alphanumeric characters with underscores
 * @param input - The string to sanitize
 * @returns The sanitized string
 */
function sanitizeString(input: string): string {
  return input.replace(/[^a-zA-Z0-9]/g, '_');
}

export type Product = DetailProduct | OrderTemplateItemProduct;

/**
 * Determines the active promo for a product based on priority and qualification rules
 * @param product - The product object with promo data
 * @param quantity - The quantity to check for bundled promo qualification (default: 1)
 * @returns Object containing the active promo and its type, or null if no active promo
 */
export const getActivePromo = (
  product: Product | undefined,
  quantity = 1
): {
  promo:
    | NonNullable<Product['discount_promos']>[0]
    | NonNullable<Product['bundled_discount_promos']>[0]
    | NonNullable<Product['free_good_promos']>[0]
    | NonNullable<Product['bundled_free_good_promos']>[0];
  type: 'bundled_discount' | 'discount' | 'bundled_free_goods' | 'free_goods';
} | null => {
  if (!product) return null;

  const discountPromo = product?.discount_promos?.[0];
  const bundledDiscountPromo = product?.bundled_discount_promos?.[0];
  const freeGoods = product?.free_good_promos?.[0];
  const bundledFreeGoods = product?.bundled_free_good_promos?.[0];

  // Determine which bundled promos are applicable based on quantity
  const shouldUseBundledDiscount =
    bundledDiscountPromo &&
    bundledDiscountPromo.sum_minimum_order_quantity &&
    quantity === bundledDiscountPromo.sum_minimum_order_quantity / 1000;

  const shouldUseBundledFreeGoods =
    bundledFreeGoods &&
    bundledFreeGoods.sum_minimum_order_quantity &&
    quantity === bundledFreeGoods.sum_minimum_order_quantity / 1000;

  // Priority order for active promo (only if qualified)
  if (shouldUseBundledDiscount) {
    return { promo: bundledDiscountPromo, type: 'bundled_discount' };
  } else if (discountPromo) {
    return { promo: discountPromo, type: 'discount' };
  } else if (shouldUseBundledFreeGoods) {
    return { promo: bundledFreeGoods, type: 'bundled_free_goods' };
  } else if (freeGoods) {
    return { promo: freeGoods, type: 'free_goods' };
  }

  return null;
};

/**
 * Determines the active promo for a product based on priority and qualification rules
 * @param product - The product object with promo data
 * @returns Object containing the promo and its type, or null if no active promo
 */
export const getPromoIgnoreQTY = (
  product: Product | undefined
): {
  promo:
    | NonNullable<Product['discount_promos']>[0]
    | NonNullable<Product['bundled_discount_promos']>[0]
    | NonNullable<Product['free_good_promos']>[0]
    | NonNullable<Product['bundled_free_good_promos']>[0];
  type: 'bundled_discount' | 'discount' | 'bundled_free_goods' | 'free_goods';
} | null => {
  if (!product) return null;

  const discountPromo = product?.discount_promos?.[0];
  const bundledDiscountPromo = product?.bundled_discount_promos?.[0];
  const freeGoods = product?.free_good_promos?.[0];
  const bundledFreeGoods = product?.bundled_free_good_promos?.[0];

  // Determine which bundled promos are applicable based on quantity
  const shouldUseBundledDiscount = bundledDiscountPromo;

  const shouldUseBundledFreeGoods = bundledFreeGoods;

  // Priority order for active promo (only if qualified)
  if (shouldUseBundledDiscount) {
    return { promo: bundledDiscountPromo, type: 'bundled_discount' };
  } else if (discountPromo) {
    return { promo: discountPromo, type: 'discount' };
  } else if (shouldUseBundledFreeGoods) {
    return { promo: bundledFreeGoods, type: 'bundled_free_goods' };
  } else if (freeGoods) {
    return { promo: freeGoods, type: 'free_goods' };
  }

  return null;
};

type DefaultTypes =
  | string
  | number
  | boolean
  | undefined
  | null
  | Record<string, unknown>;

export interface ItemCardTrackingOptions {
  index?: number;
  section_index?: string;
}

export interface ItemCardTrackingParams {
  [key: string]: string | number | boolean | null | undefined;
  item_id?: number | string;
  item_name?: string;
  item_brand?: string;
  ui_item_id?: number | string;
  ui_item_name?: string;
  ui_item_brand?: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  quantity: number;
  discount: number | null;
  promotion_id?: number | string | null;
  promotion_name?: string | null;
  section_name?: string;
  page_name?: string;
  item_list_id?: string;
  index?: number;
  section_index?: string;
}

export interface EcommerceItemParams {
  item_id?: string | number;
  item_name?: string;
  item_brand?: string;
  ui_item_id?: number | string;
  ui_item_name?: string;
  ui_item_brand?: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  section_index?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string | number;
  price?: number;
  quantity?: number;
}

/**
 * Calculates the final price of a product considering active promotions
 * @param product - The product object
 * @param quantity - The quantity of the product
 * @returns The final price after applying applicable promotions
 */
export const calculateFinalPrice = (
  product: Product | undefined,
  quantity: number
): number => {
  if (!product) return 0;

  const discountPromo = product.discount_promos?.[0];
  const bundledDiscountPromo = product.bundled_discount_promos?.[0];
  const freeGoods = product.free_good_promos?.[0];
  const bundledFreeGoods = product.bundled_free_good_promos?.[0];

  // Determine which bundled promos are applicable based on quantity
  const shouldUseBundledDiscount =
    bundledDiscountPromo &&
    bundledDiscountPromo.sum_minimum_order_quantity &&
    quantity === bundledDiscountPromo.sum_minimum_order_quantity / 1000;

  const shouldUseBundledFreeGoods =
    bundledFreeGoods &&
    bundledFreeGoods.sum_minimum_order_quantity &&
    quantity === bundledFreeGoods.sum_minimum_order_quantity / 1000;

  let promoType:
    | 'bundled_discount'
    | 'discount'
    | 'bundled_free_goods'
    | 'free_goods'
    | null = null;

  // Determine active promo for price calculation (only if qualified)
  if (shouldUseBundledDiscount) {
    promoType = 'bundled_discount';
  } else if (discountPromo) {
    promoType = 'discount';
  } else if (shouldUseBundledFreeGoods) {
    promoType = 'bundled_free_goods';
  } else if (freeGoods) {
    promoType = 'free_goods';
  }

  // Calculate price based on promo type
  const regularPrice = product.prices?.[0]?.price || 0;
  let finalPrice = regularPrice;

  if (promoType === 'bundled_discount' && bundledDiscountPromo) {
    const discountAmount = bundledDiscountPromo.deduction_amount || 0;
    finalPrice = regularPrice - discountAmount;
  } else if (promoType === 'discount' && discountPromo) {
    if (
      'price_after_discount' in discountPromo &&
      discountPromo.price_after_discount !== null
    ) {
      finalPrice = discountPromo.price_after_discount;
    } else {
      const discountAmount = discountPromo.deduction_amount || 0;
      finalPrice = regularPrice - discountAmount;
    }
  }
  // For free goods, price remains regular price

  return finalPrice;
};

/**
 * Generates section_index in format "s01", "s02", "s03", etc.
 * @param sectionNumber - 1-based section number (1, 2, 3...)
 */
export const generateSectionIndex = (sectionNumber: number): string => {
  return `s${String(sectionNumber).padStart(2, '0')}`;
};

export const getItemCardTrackingParams = (
  product: Product | undefined,
  quantity = 1,
  section?: string,
  page?: string,
  sectionId?: string,
  promoSection?: {
    promo_name?: string;
    promo_code?: string;
    mechanism?: string;
  },
  options?: ItemCardTrackingOptions
): ItemCardTrackingParams => {
  const discountPromo = product?.discount_promos?.[0];
  const bundledDiscountPromo = product?.bundled_discount_promos?.[0];
  const freeGoods = product?.free_good_promos?.[0];
  const bundledFreeGoods = product?.bundled_free_good_promos?.[0];

  if (!IS_PROD) {
    // console.log('getItemCardTrackingParams', product);
  }

  // Determine which bundled promos are applicable based on quantity
  const shouldUseBundledDiscount =
    bundledDiscountPromo &&
    bundledDiscountPromo.sum_minimum_order_quantity &&
    quantity === bundledDiscountPromo.sum_minimum_order_quantity / 1000;

  const shouldUseBundledFreeGoods =
    bundledFreeGoods &&
    bundledFreeGoods.sum_minimum_order_quantity &&
    quantity === bundledFreeGoods.sum_minimum_order_quantity / 1000;

  // Priority order for promotion tracking (send promo info even if not qualified)
  let promoForTracking:
    | typeof bundledDiscountPromo
    | typeof discountPromo
    | typeof bundledFreeGoods
    | typeof freeGoods
    | null = null;

  // Priority order: bundled_discount_promos > discount_promos > bundled_free_good_promos > free_good_promos
  let activePromo:
    | typeof bundledDiscountPromo
    | typeof discountPromo
    | typeof bundledFreeGoods
    | typeof freeGoods
    | null = null;

  let promoType:
    | 'bundled_discount'
    | 'discount'
    | 'bundled_free_goods'
    | 'free_goods'
    | null = null;

  // Determine promo for tracking (always send if exists, regardless of qualification)
  if (bundledDiscountPromo) {
    promoForTracking = bundledDiscountPromo;
  } else if (discountPromo) {
    promoForTracking = discountPromo;
  } else if (bundledFreeGoods) {
    promoForTracking = bundledFreeGoods;
  } else if (freeGoods) {
    promoForTracking = freeGoods;
  }

  // Determine active promo for price/discount calculation (only if qualified)
  if (shouldUseBundledDiscount) {
    activePromo = bundledDiscountPromo;
    promoType = 'bundled_discount';
  } else if (discountPromo) {
    activePromo = discountPromo;
    promoType = 'discount';
  } else if (shouldUseBundledFreeGoods) {
    activePromo = bundledFreeGoods;
    promoType = 'bundled_free_goods';
  } else if (freeGoods) {
    activePromo = freeGoods;
    promoType = 'free_goods';
  }

  const sendPromoType = () => {
    return promoForTracking;
  };

  // Calculate price and discount based on promo type
  const regularPrice = product?.prices?.[0]?.price || 0;
  const finalPrice = calculateFinalPrice(product, quantity);

  // Calculate discount amount based on final vs regular price
  let discountAmount: number | null =
    regularPrice > finalPrice ? regularPrice - finalPrice : null;

  // For free goods promos, set discount to null
  if (promoType === 'bundled_free_goods' || promoType === 'free_goods') {
    discountAmount = null;
  }

  // Naming rules (canonical GA4 spec):
  // - item_list_name = the section/list label itself
  //     Promo Highlight     -> "Promo Highlight"
  //     Product Highlight   -> "Product Highlight | <Highlight Name>" (= section)
  //     generic section     -> section
  // - item_list_id = the composite identifier
  //     Product Highlight   -> "Product Highlight | <group_id>"
  //     Promo (with promo)  -> "<section> | <brand|promo_name> | <mechanism>"
  //     generic section     -> section

  const generateItemListName = (): string => {
    if (!section) return '-';
    return section;
  };

  const generateItemListNameId = (): string => {
    if (!section) return '-';

    if (section.toLowerCase().includes('product highlight')) {
      const base = section.includes(' | ') ? section.split(' | ')[0] : section;
      return sectionId ? `${base} | ${sectionId}` : section;
    }

    if (promoSection && promoSection.promo_name && promoSection.mechanism) {
      return `${section} | ${promoSection.promo_name} | ${promoSection.mechanism}`;
    }

    if (promoForTracking) {
      const promoBrand = product?.brand_name || 'Unknown Promo';
      const mechanism = promoForTracking.mechanism || 'Unknown Mechanism';
      return `${section} | ${promoBrand} | ${mechanism}`;
    }

    return section;
  };

  // Get banner attribution if available and event doesn't already have banner fields
  const bannerAttribution = getBannerRefererSafe();

  const item_list_id = generateItemListNameId();
  const item_list_name = generateItemListName();

  return {
    item_id: product?.item_code?.toString(),
    item_name: product?.title,
    item_brand: product?.brand_name,
    item_category: product?.category_name,
    item_category2: product?.sub_category_name,
    price: finalPrice,
    quantity,
    discount: discountAmount,
    promotion_id:
      sendPromoType() !== null
        ? `Promo - ${product?.item_code} | ${sendPromoType()?.mechanism}`
        : null,
    promotion_name: sendPromoType()
      ? `Promo - ${sendPromoType()?.mechanism}`
      : null,
    voucher_id: '-',
    voucher_name: '-',
    section_name: section,
    page_name: page || section,
    currency: 'IDR',
    discount_percentage:
      activePromo &&
      regularPrice > 0 &&
      discountAmount !== null &&
      discountAmount > 0
        ? (discountAmount / regularPrice) * 100
        : null,
    item_list_id: bannerAttribution
      ? `Banner | ${bannerAttribution.banner_id}`
      : item_list_id,
    item_list_name: bannerAttribution
      ? `Banner | ${bannerAttribution.banner_name}`
      : item_list_name,
    ...(options?.index !== undefined ? { index: options.index } : {}),
    ...(options?.section_index !== undefined
      ? { section_index: options.section_index }
      : {}),
  };
};


const ECOMMERCE_SECTION_INDEX_EVENTS = new Set([
  'view_item',
  'view_item_list',
  'view_promotion',
  'view_voucher',
  'select_item',
  'select_promotion',
  'pre_add_to_cart',
  'add_to_cart',
]);

/**
 * Resolves section_index for ecommerce events from top-level params or items[].
 * GA4 DebugView surfaces event-level params; item-nested section_index alone is easy to miss.
 */
export const resolveEcommerceSectionIndex = (
  params: Record<
    string,
    DefaultTypes | DefaultTypes[] | EcommerceItemParams[]
  >
): string | undefined => {
  const topLevel = params['section_index'];
  if (topLevel !== undefined && topLevel !== null && topLevel !== '') {
    return String(topLevel);
  }

  if (Array.isArray(params['items']) && params['items'].length > 0) {
    for (const item of params['items'] as EcommerceItemParams[]) {
      if (
        item.section_index !== undefined &&
        item.section_index !== null &&
        item.section_index !== ''
      ) {
        return String(item.section_index);
      }
    }
  }

  return undefined;
};

/**
 * Tracks an event with Firebase Analytics with automatic banner attribution
 *
 * This function automatically appends banner attribution data (banner_id, banner_name, referer_source)
 * to ALL tracking events when the user's session originated from a valid banner click.
 *
 * @param eventName - The name of the event to track
 * @param params - Event parameters (banner fields will be auto-added if entry point was from banner)
 * @param options - Additional options for parameter validation and defaults
 *
 * Banner Attribution Logic:
 * - Checks localStorage for valid banner referer data (15-minute expiration)
 * - Only adds banner fields if they're not already present in params (preserves explicit tracking)
 * - Works across tabs and after external redirects due to localStorage persistence
 *
 * @example
 * // Will automatically include banner attribution if user came from banner
 * trackEvent('view_item', { product_id: '123' });
 *
 * // Will skip auto-attribution since banner_id is already specified
 * trackEvent('add_to_cart', { banner_id: 'custom_banner' });
 */
export const trackEvent = (
  eventName: string,
  params: Record<
    string,
    DefaultTypes | DefaultTypes[] | EcommerceItemParams[]
  > = {},
  options?: {
    parameterMapping?: Record<string, string[]>;
    defaultSectionNames?: Record<string, string>;
  }
) => {
  const session = localStorage.getItem('session-store');
  const shipto = localStorage.getItem('shipto-store');
  const parseUser = session ? JSON.parse(session)?.state : {};
  const parseShipTo = shipto ? JSON.parse(shipto)?.state : {};
  const buyerContext = getBuyerAnalyticsContext(parseShipTo?.selectedShipTo);

  // Set default section_name based on event type if not provided
  const defaultSectionName = options?.defaultSectionNames?.[eventName] || '-';

  // Set default page_name to 'homepage' if not provided
  const defaultPageName = 'Fallback Page Name';

  // Get banner attribution if available and event doesn't already have banner fields
  let bannerAttribution = null;
  if (
    !params['banner_id'] &&
    !params['banner_name'] &&
    !params['referer_source']
  ) {
    bannerAttribution = getBannerRefererSafe();
  }

  // Auto-extract ui_item fields for specific event tracking payloads
  let additionalUiParams: Record<string, string | number> = {};
  const uiTargetEvents = [
    'select_item',
    'view_item',
    'view_promotion',
    'select_promotion',
    'pre_add_to_cart',
    'add_to_cart',
    'remove_from_cart',
    'add_to_wishlist',
    'click_remove_wishlist',
    'click_product_to_recipe',
    'add_to_shopping_list',
    'delete_item_shopping_list',
    'click_recipe_product',
    'click_product_related',
    'click_promo_detail',
  ];

  if (uiTargetEvents.includes(eventName)) {
    let uiItemIds: string[] = [];
    let uiItemNames: string[] = [];
    let uiItemBrands: string[] = [];

    // Prioritize extracting from items array
    if (Array.isArray(params?.['items']) && params['items'].length > 0) {
      const items = params['items'] as EcommerceItemParams[];
      uiItemIds = items
        .map((i) => i.item_id)
        .filter(Boolean)
        .map(String);
      uiItemNames = items
        .map((i) => i.item_name)
        .filter(Boolean)
        .map(String);
      uiItemBrands = items
        .map((i) => i.item_brand)
        .filter(Boolean)
        .map(String);
    } else {
      // Fallback for flat structure payloads like SELECT_ITEM
      if (params?.['item_id']) uiItemIds.push(String(params['item_id']));
      if (params?.['item_name']) uiItemNames.push(String(params['item_name']));
      if (params?.['item_brand'])
        uiItemBrands.push(String(params['item_brand']));
    }

    if (uiItemIds.length > 0) {
      const joinedIds = uiItemIds.join(',');
      const joinedNames = uiItemNames.join(',');
      const joinedBrands = uiItemBrands.join(',');

      // Ensuring Firebase 100 char limit on string parameter values
      const truncate = (value: string) =>
        value.length > 100 ? value.substring(0, 100) : value;

      additionalUiParams = {
        ui_item_id: params?.['ui_item_id']
          ? String(params['ui_item_id'])
          : truncate(joinedIds),
        ui_item_name: params?.['ui_item_name']
          ? String(params['ui_item_name'])
          : truncate(joinedNames),
        ui_item_brand: params?.['ui_item_brand']
          ? String(params['ui_item_brand'])
          : truncate(joinedBrands),
      };
    }
  }

  const sectionIndex = ECOMMERCE_SECTION_INDEX_EVENTS.has(eventName)
    ? resolveEcommerceSectionIndex(params)
    : undefined;

  const eventParams = {
    ...additionalUiParams,
    item_list_id: bannerAttribution
      ? `Banner | ${bannerAttribution.banner_id}`
      : params?.['section_name'] || '-',
    item_list_name: bannerAttribution
      ? `Banner | ${bannerAttribution.banner_name}`
      : params?.['section_name'] || '-',
    ...params,
    // Add banner attribution if available and not already specified
    ...(bannerAttribution && {
      banner_id: bannerAttribution.banner_id,
      banner_name: bannerAttribution.banner_name,
      referer_source: bannerAttribution.referer_source,
    }),
    platform: 'website',
    user_id: parseUser.userId ?? '-',
    shipto_no: buyerContext.shipto_no,
    shipto_name: buyerContext.shipto_name,
    shipto_dist_channel: buyerContext.shipto_dist_channel,
    section_name: params?.['section_name'] ?? defaultSectionName,
    page_name:
      (params?.['page_name'] || params?.['section_name']) ?? defaultPageName,
    cg3_name: buyerContext.cg3_name,
    cg3_code: buyerContext.cg3_code,
    ...(sectionIndex !== undefined ? { section_index: sectionIndex } : {}),
  };

  // Validate required parameters for specific event types
  const requiredParams = options?.parameterMapping?.[eventName] || [];
  requiredParams.forEach((param: string) => {
    if (!params[param] && !IS_PROD) {
      console.warn(
        `Warning: Parameter '${param}' is recommended for event '${eventName}'`
      );
    }
  });

  const name = sanitizeString(eventName);

  if (typeof window !== 'undefined') {
    try {
      const app = getFirebaseApp();
      if (app) {
        const analytics = getAnalytics(app);
        logEvent(analytics, name, eventParams);
        if (!IS_PROD) {
          console.log(`Success tracking event: ${name}`, eventParams);
        }
      } else {
        console.error('Firebase app is not available.');
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  } else {
    console.error('Event analytics is not available.');
  }
};

/**
 * Tracks a page view event with Google Analytics
 *
 * @param pageName - The name of the page to track
 * @param url - The URL of the page (defaults to '/')
 * @param params - Additional parameters to include with the event
 *
 * @example
 * // Track a page view for the homepage
 * trackPageView('homepage', '/');
 *
 * @example
 * // Track a page view with additional parameters
 * trackPageView('product_detail', '/product/123', { product_id: '123' });
 */
export const trackPageView = (
  pageName: string,
  url = '/',
  params?: { [key: string]: string | number | boolean | undefined }
) => {
  const name = sanitizeString(pageName);

  const eventParams = {
    ...params,
    page: name,
    url,
    platform: 'website',
  };

  if (typeof window !== 'undefined') {
    try {
      const app = getFirebaseApp();
      if (app) {
        const analytics = getAnalytics(app);
        logEvent(analytics, 'page_view', eventParams);
        if (!IS_PROD) {
          console.log(`Tracking page view: ${name}`, eventParams);
        }
      } else {
        console.error('Firebase app is not available.');
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  } else {
    console.error('Event analytics is not available.');
  }
};
